import { useState } from 'react'
import {
  Mail,
  MessageSquare,
  Phone,
  Clock,
  HelpCircle,
  BookOpen,
  FileText,
  Video,
  Download,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Info,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Send,
  Copy,
  Check
} from 'lucide-react'

const Support = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [expandedFaq, setExpandedFaq] = useState(null)
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'medium'
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [copiedEmail, setCopiedEmail] = useState(false)

  const supportEmail = 'info@talkida.co.in'

  const categories = [
    { id: 'all', name: 'All Topics', count: 0 },
    { id: 'getting-started', name: 'Getting Started', count: 5 },
    { id: 'billing', name: 'Billing & Plans', count: 3 },
    { id: 'technical', name: 'Technical Issues', count: 8 },
    { id: 'widgets', name: 'Widgets & Integration', count: 6 },
    { id: 'api', name: 'API & Development', count: 4 },
    { id: 'account', name: 'Account Management', count: 3 }
  ]

  const faqs = [
    {
      id: 1,
      category: 'getting-started',
      question: 'How do I create my first chatbot?',
      answer: 'To create your first chatbot, go to the Bots section in your dashboard, click "Create New Bot", and follow the step-by-step wizard. You can choose from various templates or start from scratch.',
      tags: ['bot', 'creation', 'tutorial']
    },
    {
      id: 2,
      category: 'getting-started',
      question: 'What is the difference between the free and paid plans?',
      answer: 'The free plan includes up to 3 chatbots and 100 messages per month. Paid plans offer unlimited chatbots, more messages, advanced analytics, API access, and priority support.',
      tags: ['pricing', 'plans', 'features']
    },
    {
      id: 3,
      category: 'widgets',
      question: 'How do I integrate the chat widget on my website?',
      answer: 'Copy the widget code from the Widgets section and paste it before the closing </body> tag of your website. The widget will automatically load and connect to your selected chatbot.',
      tags: ['widget', 'integration', 'website']
    },
    {
      id: 4,
      category: 'technical',
      question: 'Why is my chatbot not responding?',
      answer: 'Check if your chatbot is active, verify the API key is correct, ensure your plan hasn\'t exceeded message limits, and check the connection status in your dashboard.',
      tags: ['troubleshooting', 'connection', 'api']
    },
    {
      id: 5,
      category: 'billing',
      question: 'How do I upgrade my plan?',
      answer: 'Go to the Billing section in your dashboard, select your desired plan, and follow the upgrade process. Changes take effect immediately.',
      tags: ['billing', 'upgrade', 'payment']
    },
    {
      id: 6,
      category: 'api',
      question: 'How do I use the API to send messages?',
      answer: 'Use the REST API endpoint with your API key. Send POST requests to /api/chat with your message and chatbot ID. Check the API documentation for detailed examples.',
      tags: ['api', 'development', 'integration']
    },
    {
      id: 7,
      category: 'account',
      question: 'How do I reset my password?',
      answer: 'Click "Forgot Password" on the sign-in page, enter your email address, and follow the instructions in the email we send you.',
      tags: ['password', 'security', 'account']
    },
    {
      id: 8,
      category: 'technical',
      question: 'The widget is not loading on my website',
      answer: 'Ensure the script is placed correctly, check for JavaScript errors in your browser console, verify your domain is whitelisted, and try clearing your browser cache.',
      tags: ['widget', 'loading', 'troubleshooting']
    }
  ]

  const resources = [
    {
      title: 'Getting Started Guide',
      description: 'Complete guide to setting up your first chatbot',
      type: 'guide',
      icon: BookOpen,
      url: '#',
      download: false
    },
    {
      title: 'API Documentation',
      description: 'Comprehensive API reference and examples',
      type: 'documentation',
      icon: FileText,
      url: '#',
      download: false
    },
    {
      title: 'Widget Integration Video',
      description: 'Step-by-step video tutorial for widget setup',
      type: 'video',
      icon: Video,
      url: '#',
      download: false
    },
    {
      title: 'Best Practices PDF',
      description: 'Download our chatbot best practices guide',
      type: 'pdf',
      icon: Download,
      url: '#',
      download: true
    }
  ]

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory
    const matchesSearch = searchQuery === '' ||
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    return matchesCategory && matchesSearch
  })

  const handleFaqToggle = (faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId)
  }

  const handleContactSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000))

    setIsSubmitting(false)
    setIsSubmitted(true)

    // Reset form after 3 seconds
    setTimeout(() => {
      setIsSubmitted(false)
      setContactForm({
        name: '',
        email: '',
        subject: '',
        message: '',
        priority: 'medium'
      })
    }, 3000)
  }

  const copyEmailToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(supportEmail)
      setCopiedEmail(true)
      setTimeout(() => setCopiedEmail(false), 2000)
    } catch (err) {
      console.error('Failed to copy email:', err)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Help & Support</h1>
          <p className="text-secondary-400">Get help with Talkida and find answers to common questions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Contact Information */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Mail className="w-5 h-5 mr-2" />
                Contact Support
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary-500/20 rounded-lg">
                      <Mail className="w-5 h-5 text-primary-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Email Support</h3>
                      <div className="flex items-center space-x-2">
                        <span className="text-secondary-400">{supportEmail}</span>
                        <button
                          onClick={copyEmailToClipboard}
                          className="p-1 hover:bg-secondary-700 rounded transition-colors"
                          title="Copy email"
                        >
                          {copiedEmail ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4 text-secondary-400" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-500/20 rounded-lg">
                      <Clock className="w-5 h-5 text-green-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Response Time</h3>
                      <p className="text-secondary-400">Within 24 hours</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <MessageSquare className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Live Chat</h3>
                      <p className="text-secondary-400">Available 9 AM - 6 PM EST</p>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-500/20 rounded-lg">
                      <HelpCircle className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Priority Support</h3>
                      <p className="text-secondary-400">Pro & Enterprise plans</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Search className="w-5 h-5 mr-2" />
                Search Knowledge Base
              </h2>

              <div className="space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Search for help articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    />
                  </div>
                  <div className="md:w-48">
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    >
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name} {category.count > 0 && `(${category.count})`}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <HelpCircle className="w-5 h-5 mr-2" />
                Frequently Asked Questions
              </h2>

              <div className="space-y-4">
                {filteredFaqs.map((faq) => (
                  <div key={faq.id} className="border border-secondary-700 rounded-lg">
                    <button
                      onClick={() => handleFaqToggle(faq.id)}
                      className="w-full px-4 py-4 text-left flex items-center justify-between hover:bg-secondary-700 transition-colors"
                    >
                      <span className="text-white font-medium">{faq.question}</span>
                      {expandedFaq === faq.id ? (
                        <ChevronDown className="w-5 h-5 text-secondary-400" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-secondary-400" />
                      )}
                    </button>
                    {expandedFaq === faq.id && (
                      <div className="px-4 pb-4 border-t border-secondary-700">
                        <p className="text-secondary-300 mt-4">{faq.answer}</p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {faq.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-primary-500/20 text-primary-400 text-xs rounded-full"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-6 flex items-center">
                <Send className="w-5 h-5 mr-2" />
                Send us a Message
              </h2>

              {isSubmitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">Message Sent!</h3>
                  <p className="text-secondary-400">We'll get back to you within 24 hours.</p>
                </div>
              ) : (
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Name</label>
                      <input
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">Email</label>
                      <input
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Subject</label>
                    <input
                      type="text"
                      required
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                      className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                      placeholder="Brief description of your issue"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Priority</label>
                    <select
                      value={contactForm.priority}
                      onChange={(e) => setContactForm({ ...contactForm, priority: e.target.value })}
                      className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all"
                    >
                      <option value="low">Low - General inquiry</option>
                      <option value="medium">Medium - Feature request</option>
                      <option value="high">High - Bug report</option>
                      <option value="urgent">Urgent - Service down</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Message</label>
                    <textarea
                      required
                      rows={6}
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      className="w-full px-4 py-3 bg-secondary-700 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 transition-all resize-none"
                      placeholder="Please describe your issue in detail..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full px-6 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Links */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Quick Links</h3>
              <div className="space-y-3">
                <a href="#" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700 transition-colors">
                  <BookOpen className="w-5 h-5 text-primary-400" />
                  <span className="text-white">Documentation</span>
                  <ExternalLink className="w-4 h-4 text-secondary-400 ml-auto" />
                </a>
                <a href="#" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700 transition-colors">
                  <Video className="w-5 h-5 text-primary-400" />
                  <span className="text-white">Video Tutorials</span>
                  <ExternalLink className="w-4 h-4 text-secondary-400 ml-auto" />
                </a>
                <a href="#" className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700 transition-colors">
                  <FileText className="w-5 h-5 text-primary-400" />
                  <span className="text-white">API Reference</span>
                  <ExternalLink className="w-4 h-4 text-secondary-400 ml-auto" />
                </a>
              </div>
            </div>

            {/* Resources */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Resources</h3>
              <div className="space-y-3">
                {resources.map((resource, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-secondary-700 transition-colors">
                    <resource.icon className="w-5 h-5 text-primary-400" />
                    <div className="flex-1">
                      <h4 className="text-white text-sm font-medium">{resource.title}</h4>
                      <p className="text-secondary-400 text-xs">{resource.description}</p>
                    </div>
                    {resource.download ? (
                      <Download className="w-4 h-4 text-secondary-400" />
                    ) : (
                      <ExternalLink className="w-4 h-4 text-secondary-400" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Status */}
            <div className="bg-secondary-800 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-white mb-4">System Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">API Status</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-400 text-sm">Operational</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">Widget Service</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-400 text-sm">Operational</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm">Database</span>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-green-400 text-sm">Operational</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Support
